import type { Envelope } from '@cp/shared';

/**
 * The HTTP client for the Control Plane API.
 *
 * Every response is an envelope, so unwrapping happens once here rather than in
 * every caller, and an error arrives as a thrown `ApiError` carrying the stable
 * code the backend promised (TZ section 17). The modules beside this one — one
 * per resource — are its only callers; components reach them through hooks.
 */

export class ApiError extends Error {
  readonly code: string;
  readonly details: Record<string, unknown>;
  readonly requestId: string | null;
  readonly status: number;

  constructor(init: {
    code: string;
    message: string;
    details: Record<string, unknown>;
    requestId: string | null;
    status: number;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.details = init.details;
    this.requestId = init.requestId;
    this.status = init.status;
  }
}

const CSRF_HEADER = 'x-csrf-token';
let csrfToken: string | null = null;

/**
 * Fetches a CSRF token once and reuses it. Mutations need it; reads do not, so
 * the first read of a session does not pay for it.
 */
async function ensureCsrf(): Promise<string> {
  if (csrfToken) return csrfToken;

  const response = await fetch('/api/v1/auth/csrf', { credentials: 'same-origin' });
  const body = (await response.json()) as Envelope<{ csrfToken: string }>;
  csrfToken = body.data?.csrfToken ?? '';
  return csrfToken;
}

/**
 * Forgets the cached token. The cookie it is paired with belongs to the session
 * that just ended, so keeping it would make the first mutation after the next
 * sign-in fail for a reason nobody could see.
 */
export function forgetCsrf(): void {
  csrfToken = null;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Required by every state-changing route, and deliberately not generated
   *  here: it has to survive a retry, so the caller owns it. */
  idempotencyKey?: string;
  signal?: AbortSignal;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {};

  // Only when there is something to describe. Declaring a JSON body and sending
  // none makes the server reject the request before any handler sees it, which
  // is how an action like signing out fails for a reason nothing on screen can
  // explain.
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  if (method !== 'GET') headers[CSRF_HEADER] = await ensureCsrf();
  if (options.idempotencyKey) headers['idempotency-key'] = options.idempotencyKey;

  const response = await fetch(`/api/v1${path}`, {
    method,
    headers,
    // The session is an httpOnly cookie; nothing here reads or sends it directly.
    credentials: 'same-origin',
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  const body = await readEnvelope<T>(response);
  return body.data;
}

/**
 * A file sent as it is, not as JSON (ADR-0044): its digest in a header, so a
 * transfer that arrived damaged is refused rather than stored.
 */
export async function apiUpload<T>(path: string, file: Blob, sha256: string): Promise<T> {
  const response = await fetch(`/api/v1${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/octet-stream', 'x-content-sha256': sha256, [CSRF_HEADER]: await ensureCsrf() },
    credentials: 'same-origin',
    body: file,
  });
  const body = await readEnvelope<T>(response);
  return body.data;
}

/** A page of a list, with its total when the server counts one. */
export interface Page<T> {
  items: T[];
  nextCursor: string | null;
  total: number | null;
}

/**
 * Reads the paging metadata as well as the rows.
 *
 * `apiRequest` unwraps to `data`, which is right for almost everything and
 * wrong for a list whose total the interface needs to show.
 */
export async function apiPage<T>(path: string): Promise<Page<T>> {
  const response = await fetch(`/api/v1${path}`, { credentials: 'same-origin' });
  const body = await readEnvelope<T[]>(response);

  return {
    items: body.data ?? [],
    nextCursor: body.meta?.nextCursor ?? null,
    total: body.meta?.total ?? null,
  };
}

/** The envelope's data side, or the error it carries, thrown as an `ApiError`. */
async function readEnvelope<T>(response: Response): Promise<Extract<Envelope<T>, { error: null }>> {
  let body: Envelope<T>;
  try {
    body = (await response.json()) as Envelope<T>;
  } catch {
    throw new ApiError({
      code: 'INTERNAL_ERROR',
      message: 'The server sent a response this client could not read',
      details: {},
      requestId: null,
      status: response.status,
    });
  }

  if (body.error) {
    // The request id travels with the error so an operator reporting a problem
    // can name the exact request in the logs.
    throw new ApiError({
      code: body.error.code,
      message: body.error.message,
      details: body.error.details ?? {},
      requestId: body.meta?.requestId ?? null,
      status: response.status,
    });
  }

  return body;
}

/** `path?a=1&b=2` from the parameters that are set; `path` alone when none are. */
export function withQuery(path: string, params: URLSearchParams): string {
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}
